"use client";

import Script from "next/script";
import { useEffect, useState } from "react";

// 自托管在 umami 那台机器上（2026-10-30 起；10-31 Umami 停用后是唯一的自有统计）
const rybbitHost = "https://r.app-stats.net";
const rybbitSiteId = "7eb611a4ff90";
// Rybbit 没有 Umami 的 data-domains，服务端也不校验来源，只能在这里拦：
// localhost / 镜像站不加载脚本，也不发事件
const rybbitDomain = "imagesart.ai";
// 服务端对 properties 序列化后超过 2048 字符的事件整条 400，不是截断
const MAX_PROPERTIES_LENGTH = 2048;

// 脚本在 useEffect 之后才挂上，之前触发的事件先排在这里，onLoad 时补发。
// 脚本一执行就会装好 window.rybbit（自带排队的桩），所以 onLoad 时直接调就行
const pendingEvents: Array<[string, Record<string, unknown> | undefined]> = [];
// 同理，脚本没到之前登录态先记在这里；undefined = 还没同步过
let pendingUserId: string | null | undefined;

function isRybbitHost() {
	return (
		typeof window !== "undefined" &&
		window.location.hostname === rybbitDomain
	);
}

// 长字符串（client-error 的 message / frame 之类）逐轮减半截断，直到整包放得下
function fitProperties(data: Record<string, unknown> | undefined) {
	if (!data) {
		return undefined;
	}

	for (let limit = 500; ; limit = Math.floor(limit / 2)) {
		const props = Object.fromEntries(
			Object.entries(data).map(([key, value]) => [
				key,
				typeof value === "string" ? value.slice(0, limit) : value,
			]),
		);
		if (
			JSON.stringify(props).length <= MAX_PROPERTIES_LENGTH ||
			limit < 16
		) {
			return props;
		}
	}
}

function flushPendingEvents() {
	const rybbit = (window as any).rybbit;
	if (!rybbit) {
		return;
	}

	while (pendingEvents.length > 0) {
		const [event, data] = pendingEvents.shift() as [
			string,
			Record<string, unknown> | undefined,
		];
		rybbit.event(event, fitProperties(data));
	}

	if (pendingUserId !== undefined) {
		syncUserId(rybbit, pendingUserId);
		pendingUserId = undefined;
	}
}

// identify 每调一次就发一次请求，只在 ID 变了时调。脚本自己从 localStorage 恢复
// 上次的 ID，但要等它拉完站点配置才恢复——桩上的 getUserId 永远是 null，
// 所以用 onReady 等真正的实例再比
function syncUserId(rybbit: any, userId: string | null) {
	rybbit.onReady((api: any) => {
		const current = api.getUserId();
		if (userId && current !== userId) {
			api.identify(userId);
		} else if (!userId && current) {
			api.clearUserId();
		}
	});
}

export function RybbitAnalyticsScript() {
	const [enabled, setEnabled] = useState(false);

	useEffect(() => {
		setEnabled(isRybbitHost());
	}, []);

	if (!enabled) {
		return null;
	}

	return (
		<Script
			// 别用 id="rybbit"：元素 id 会成为 window 上的同名属性，脚本没执行完前
			// window.rybbit 就是这个 <script> 标签，调 onReady 整页崩（10/31 每小时约 100 次）
			id="rybbit-script"
			src={`${rybbitHost}/api/script.js?siteId=${rybbitSiteId}`}
			onLoad={flushPendingEvents}
		/>
	);
}

export function trackRybbitEvent(
	event: string,
	data?: Record<string, unknown>,
) {
	if (!isRybbitHost()) {
		return;
	}

	const rybbit = (window as any).rybbit;
	if (!rybbit) {
		pendingEvents.push([event, data]);
		return;
	}

	rybbit.event(event, fitProperties(data));
}

// 只传我们库里的 user.id，不传邮箱名字等 traits，Rybbit 里的事件就能按 id 直接和 Neon 对上。
// 登录前同一设备的事件 Rybbit 会自己回填上这个 id
export function identifyRybbitUser(userId: string | null) {
	if (!isRybbitHost()) {
		return;
	}

	const rybbit = (window as any).rybbit;
	if (!rybbit) {
		pendingUserId = userId;
		return;
	}

	syncUserId(rybbit, userId);
}

// 对应 trackUmamiEventBeacon：global-error 换掉了根 layout，脚本不会再加载，
// 直接 POST 到收数接口；keepalive 让它在页面卸载后也能发出去。
// 返回是否被收下，注册回执靠它决定清不清 cookie
export async function trackRybbitEventBeacon(
	event: string,
	data?: Record<string, unknown>,
): Promise<boolean> {
	if (!isRybbitHost()) {
		return false;
	}

	const properties = fitProperties(data);
	try {
		const response = await fetch(`${rybbitHost}/api/track`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			keepalive: true,
			body: JSON.stringify({
				type: "custom_event",
				site_id: rybbitSiteId,
				hostname: window.location.hostname,
				pathname: window.location.pathname,
				querystring: window.location.search,
				page_title: document.title,
				referrer: document.referrer,
				language: navigator.language,
				screenWidth: window.screen.width,
				screenHeight: window.screen.height,
				event_name: event,
				properties: properties ? JSON.stringify(properties) : undefined,
			}),
		});
		return response.ok;
	} catch {
		return false;
	}
}
